import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

export default function NoteworthyBullsPage() {
  const [items, setItems] = useState([]);
  const [sires, setSires] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    document.title = "Semen Directory & Featured Bloodlines — Bulls of the Big Sky";
    (async () => {
      const [r, f, s] = await Promise.all([
        api.get("/noteworthy-bulls"),
        api.get("/featured-sires"),
        api.get("/site-settings"),
      ]);
      setItems(r.data);
      setSires(f.data);
      setSettings(s.data);
    })();
  }, []);

  return (
    <div data-testid="noteworthy-bulls-page" className="min-h-screen" style={{ background: "var(--bg-primary)" }}>
      <SiteHeader />
      <section className="pt-40 pb-12 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="eyebrow">Semen Directory  •  Working in the Industry</div>
          <h1 className="font-display text-6xl md:text-8xl lg:text-9xl mt-4 leading-[0.95]">
            Semen<br />directory.
          </h1>
          <p className="mt-8 max-w-3xl text-base md:text-lg" style={{ color: "var(--text-secondary)" }}>
            Our consignors offer bulls that work for the commercial cattleman out on the range and
            bulls that can perform as top A.I. stud herd sires. Our consignors offer semen on bulls
            purchased through the Bulls of the Big Sky Sale, and herd sires purchased from other elite
            programs across the country, such as LBRS Integrity M76, BSUM Magnitude 4183M, WS Marvel 8M,
            Fauth Landman 802L, JC Stunt Man 38M, KCC Magnum K036, GW 7&amp;7 408M, and JC Pioneer 0046N.
          </p>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-32">
        <div className="max-w-[1400px] mx-auto">
          {items.length === 0 ? (
            <Empty label="Noteworthy bulls coming soon." />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {items.map((b, i) => (
                <article key={b.id} data-testid={`noteworthy-${i}`} className="border" style={{ borderColor: "var(--border-subtle)" }}>
                  {b.photo_url && (
                    <div className="frame-image" style={{ aspectRatio: "3 / 2" }}>
                      <img src={b.photo_url} alt={b.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="eyebrow text-[10px] mb-2" style={{ color: "var(--brass-bright)" }}>
                      {b.year_sold ? `${b.year_sold}  •  ` : ""}{b.breed || "SimGenetics"}
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl leading-tight">{b.name}</h2>
                    {b.registration && (
                      <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                        ASA #{b.registration}
                      </div>
                    )}
                    {b.description && (
                      <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                        {b.description}
                      </p>
                    )}
                    <dl className="mt-5 space-y-2 text-xs">
                      {b.bred_by && (
                        <div className="flex gap-3">
                          <dt className="eyebrow-muted text-[10px] w-20 shrink-0">Bred By</dt>
                          <dd style={{ color: "var(--text-primary)" }}>{b.bred_by}</dd>
                        </div>
                      )}
                      {b.sold_to && (
                        <div className="flex gap-3">
                          <dt className="eyebrow-muted text-[10px] w-20 shrink-0">Sold To</dt>
                          <dd style={{ color: "var(--text-primary)" }}>{b.sold_to}</dd>
                        </div>
                      )}
                    </dl>
                    {b.bloodline_tags && b.bloodline_tags.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {b.bloodline_tags.map((t) => (
                          <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-1 border"
                                style={{ borderColor: "rgba(30,77,117,0.3)", color: "var(--brass)" }}>
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FEATURED BLOODLINES — merged from /sires */}
      {sires.length > 0 && (
        <section id="bloodlines" data-testid="featured-bloodlines-section" className="px-6 md:px-12 pb-32">
          <div className="max-w-[1400px] mx-auto border-t pt-16" style={{ borderColor: "var(--border-subtle)" }}>
            <div className="eyebrow mb-4">Featured Bloodlines  •  Genetics in the Lineup</div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95]">
              Featured bloodlines.
            </h2>
            <p className="mt-6 max-w-3xl text-base md:text-lg" style={{ color: "var(--text-secondary)" }}>
              The AI sires and herd bulls behind the lots in our upcoming sales. If you&apos;re tracking
              a specific bloodline, this is where you find out which Big Sky lot carries it.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-12">
              {sires.map((s, i) => (
                <article key={s.id} data-testid={`sire-${i}`} className="border" style={{ borderColor: "var(--border-subtle)" }}>
                  {s.photo_url && (
                    <div className="frame-image" style={{ aspectRatio: "3 / 2" }}>
                      <img src={s.photo_url} alt={s.name} className="w-full h-full object-cover" style={{ objectPosition: "center top" }} />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="eyebrow text-[10px] mb-2" style={{ color: "var(--brass-bright)" }}>
                      {s.breed || "SimGenetics"} {s.sale_slug && `  •  ${s.sale_slug === "legacy" ? "Legacy 2026" : "Annual 2027"}`}
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl leading-tight">{s.name}</h3>
                    {s.nickname && (
                      <div className="font-display italic text-lg mt-1" style={{ color: "var(--brass)" }}>
                        &ldquo;{s.nickname}&rdquo;
                      </div>
                    )}
                    {s.registration && (
                      <div className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>ASA #{s.registration}</div>
                    )}
                    {s.description && (
                      <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{s.description}</p>
                    )}
                    {(s.owner || s.progeny_count_label) && (
                      <dl className="mt-5 space-y-2 text-xs">
                        {s.owner && (
                          <div className="flex gap-3">
                            <dt className="eyebrow-muted text-[10px] w-16 shrink-0">Owner</dt>
                            <dd style={{ color: "var(--text-primary)" }}>{s.owner}</dd>
                          </div>
                        )}
                        {s.progeny_count_label && (
                          <div className="flex gap-3">
                            <dt className="eyebrow-muted text-[10px] w-16 shrink-0">In Sale</dt>
                            <dd style={{ color: "var(--text-primary)" }}>{s.progeny_count_label}</dd>
                          </div>
                        )}
                      </dl>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <SiteFooter settings={settings} />
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="border p-16 text-center mt-12" style={{ borderColor: "var(--border-subtle)" }}>
      <div className="font-display text-3xl mb-3">{label}</div>
      <Link to="/" className="link-brass">Back to home →</Link>
    </div>
  );
}
